import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Filter,
  // New icons for updated sectors and stakeholders
  Briefcase,
  // New icons for quick stats
  Database,
  Star,
  Package,
  X,
} from "lucide-react";
import { StageGates } from "./components/stages-gates";
import "./components/global.css";
import { InvesterProspectors } from "./components/investors-prospectors";
import { ProjectPipeine } from "./components/ProjectPipeine";
import { useSnackbar } from "../../../_theme/context/SnackbarContext";
import { useAuth } from "../auth";
import { useNavigate, useParams } from "react-router-dom";
import { envConfig, getDashboardLink } from "../../../utils/config";
import { usePageData } from "../../../_theme/layout/core";
import { DashboardMenu } from "./components/dashboardMenu";
import { PipelineQuickStat, PipelineStatus } from "./components/pipelineStatus";
import CreateProjectModal from "../apps/components/CreateProjectModal";
import { getDepartments, getDistrictMunicipality, getLocalDistrictMunicipality, getLookupDetails, getPipelineDashboardStatusData, getSectorSubSectors } from "../core/_request";
import {
  IDashboardFilterData,
  IDepartment,
  IDistrict,
  ILookup,
  IMunicipality,
  ISector,
  PipelineDepartmentData,
  PipelineOverviewData,
  PipelineSectorData,
} from "../core/_models";
import UserTable from "../user";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { MyProjectCard } from "./components/my-project-card";
import { KpiMonitoring } from "./components/kpiMonitoring";
import SGReports from "./components/sgReports";
import { useDispatch, useSelector } from "react-redux";
import { fetchDepartmentMasterAction, fetchDistrictMasterAction, fetchLocationMasterAction, fetchPriorityMasterAction, fetchSectorMasterAction } from "../../../redux/actions/master.actions";
import { AppDispatch, RootState } from "../../../redux/store";
import { getApiErrorMessage } from "../../../utils/utils";
import { isAbortedError } from "../../../utils/global";
import { useCanOpenDashboardTab, useDefaultDashboardTab } from "./core/tabRights";
import { AccessDenied } from "../apps/components/UI/RequirePermission";

const CustomBadge = ({ children, onClick }: { children: React.ReactNode, onClick: () => void }) => {
  return (
    <span className="text-primary text-decoration-line-through-hover cursor-pointer font-semibold border border-primary badge rounded-pill me-2" onClick={onClick}>{children} <X className="ms-1" size={16} /></span>
  )
}
interface BoxStyle {
  existing_pipeline: PipelineQuickStat;
  prioritised_pipeline: PipelineQuickStat;
  project_evaluation_prepacking: PipelineQuickStat;
  pipeline_portfolio: PipelineQuickStat;
}

type BoxKey =
  | "existing_pipeline"
  | "prioritised_pipeline"
  | "project_evaluation_prepacking"
  | "pipeline_portfolio";

const boxStyle: BoxStyle = {
  existing_pipeline: {
    label: "",
    value: "",
    icon: Database,
    change: "",
    gradient: "linear-gradient(to bottom right, #3B82F6, #2563EB)",
    bgGradient: "linear-gradient(135deg, #d5e4ff 0%, #e8f0ff 55%, #ffffff 100%)",
  },
  prioritised_pipeline: {
    label: "",
    value: "",
    icon: Star,
    change: "",
    gradient: "linear-gradient(to bottom right, #ff8c00, #ffbf00)",
    bgGradient: "linear-gradient(135deg, #ffedcf 0%, #fff5e5 55%, #ffffff 100%)",
  },
  project_evaluation_prepacking: {
    label: "",
    value: "",
    icon: Package,
    change: "",
    gradient: "linear-gradient(to bottom right, #22c55e, #10b981)",
    bgGradient: "linear-gradient(135deg, #cef3e7 0%, #e6f7f2 55%, #ffffff 100%)",
  },
  pipeline_portfolio: {
    label: "",
    value: "",
    icon: Briefcase,
    change: "",
    gradient: "linear-gradient(to bottom right, #a855f7, #6366f1)",
    bgGradient: "linear-gradient(135deg, #e7dcff 0%, #f2ecff 55%, #ffffff 100%)",
  },
};

const DashBoardNew = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { auth } = useAuth();
  const canOpenTab = useCanOpenDashboardTab();
  const defaultTab = useDefaultDashboardTab();
  const { type } = useParams();
  const { setSiteTitle } = usePageData();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(type ? type : defaultTab);
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [municipalityData, setMunicipalityData] = useState<IMunicipality[]>([]);
  const { prioritiesData, departmentsData, sectorsData, districtData, isDistrictDataLoading, isDepartmentsDataLoading, isSectorsDataLoading, isPrioritiesDataLoading, isLocationDataLoading } = useSelector((state: RootState) => state.masterDataReducer);

  const [filters, setFilters] = useState<IDashboardFilterData>({
    department_id: [],
    sector_id: [],
    priority_id: [],
    district_id: [],
  })

  const [AppliedFilters, setAppliedFilters] = useState<IDashboardFilterData>({
    department_id: [],
    sector_id: [],
    priority_id: [],
    district_id: [],
  })

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [quickStats, setQuickStats] = useState<PipelineQuickStat[]>(
    Object.values(boxStyle)
  );
  const [pipelineOverviewGraph, setPipelineOverviewGraph] = useState<
    PipelineOverviewData[]
  >([]);
  const [sectorChartData, setSectorChartData] = useState<PipelineSectorData>({
    categories: [],
    values: [],
    colors: [],
    budgets: [],
  });
  const [departmentChartData, setDepartmentChartData] = useState<PipelineDepartmentData>({
    names: [],
    values: [],
  });
  const projectsAbortRef = useRef<AbortController | null>(null);
  const filtersAbortRef = useRef<AbortController | null>(null);

  // Without a tab in the URL, open the first tab the user may see.
  useEffect(() => {
    setActiveTab(type ? type : defaultTab);
  }, [type, defaultTab]);

  const fetchProjects = useCallback(async () => {
    projectsAbortRef.current?.abort();
    const controller = new AbortController();
    projectsAbortRef.current = controller;

    try {
      setLoading(true);
      const [
        pipelineWebPageResp,
      ] = (await Promise.all([
        getPipelineDashboardStatusData(AppliedFilters, controller.signal),
      ]));

      if (controller.signal.aborted) return;

      if (pipelineWebPageResp.status == "ok") {
        setQuickStats(
          (Object.keys(boxStyle) as BoxKey[]).map((key) => {
            const boxData = pipelineWebPageResp.data.four_box[key];
            return {
              ...boxStyle[key],
              label: boxData.title,
              change: boxData.desc,
              value: boxData.number,
            };
          })
        );
        setPipelineOverviewGraph(
          pipelineWebPageResp.data.pipeline_overview_graph
        );
        setSectorChartData(
          pipelineWebPageResp.data.pipeline_sector_graph
        );
        setDepartmentChartData(
          pipelineWebPageResp.data.pipeline_department_graph ?? { names: [], values: [] }
        );
      } else {
        pipelineWebPageResp?.message &&
          showSnackbar(pipelineWebPageResp.message, "danger");
      }
    } catch (error) {
      if (isAbortedError(error)) return;
      console.error("Failed to fetch projects:", error);
      showSnackbar(getApiErrorMessage(error, "Failed to fetch projects:"), "danger");
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [AppliedFilters, showSnackbar]);

  const fetchFilterData = useCallback(async () => {
    filtersAbortRef.current?.abort();
    const controller = new AbortController();
    filtersAbortRef.current = controller;

    try {
      const [
        sectorResponse,
        departmentResponse,
        sectorPriorityResponse,
        districtResponse,
      ] = (await Promise.all([
        getSectorSubSectors(controller.signal),
        getDepartments(controller.signal),
        getLookupDetails('wcg_sector_priorities', '', controller.signal),
        getDistrictMunicipality(controller.signal)
      ]));

      if (controller.signal.aborted) return;

      if (sectorResponse.status === "ok") {
        dispatch(fetchSectorMasterAction(sectorResponse.data.sectors));
      } else {
        sectorResponse.message &&
          showSnackbar(sectorResponse.message, "danger");
      }

      if (departmentResponse.status === "ok") {
        dispatch(fetchDepartmentMasterAction(departmentResponse.data.departments));
      } else {
        departmentResponse.message &&
          showSnackbar(departmentResponse.message, "danger");
      }

      if (sectorPriorityResponse.status == "ok") {
        dispatch(fetchPriorityMasterAction(sectorPriorityResponse.data.list));
      } else {
        sectorPriorityResponse.message &&
          showSnackbar(sectorPriorityResponse.message, "danger");
      }
      if (districtResponse.status === "ok") {
        dispatch(fetchDistrictMasterAction(districtResponse.data));
      } else {
        districtResponse.message &&
          showSnackbar(districtResponse.message, "danger");
      }
    } catch (error) {
      if (isAbortedError(error)) return;
      console.error("Failed to fetch projects:", error);
      showSnackbar(getApiErrorMessage(error, "Failed to fetch projects:"), "danger");
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [dispatch, showSnackbar]);

  const handleFetchMunicipalityData = async (district_id: number) => {
    try {
      const municipalityResponse = await getLocalDistrictMunicipality(district_id);
      if (municipalityResponse.status === "ok") {
        setMunicipalityData(municipalityResponse.data);
      } else {
        municipalityResponse.message &&
          showSnackbar(municipalityResponse.message, "danger");
      }
    }
    catch (error) {
      console.error("Failed to fetch projects:", error);
      showSnackbar("Failed to fetch projects:", "danger");
    } finally {
      setLoading(false);
    }
  }

  const handleNavigate = (tabName: string) => {
    setActiveTab(tabName);
    navigate(getDashboardLink(tabName));
  };

  const onCreateProject = () => {
    fetchProjects();
    setRefreshData(true);
  };

  const handleFilterChange = (
    section: keyof IDashboardFilterData,
    id: number | string
  ) => {
    setFilters((prev) => {
      const selected = prev[section] as (number | string)[];

      return {
        ...prev,
        [section]: selected.includes(id)
          ? selected.filter((item) => item !== id)
          : [...selected, id],
      };
    });
  };

  const isFilterEmpty = (f: typeof filters) =>
    f.department_id.length === 0 &&
    f.sector_id.length === 0 &&
    f.priority_id.length === 0 &&
    f.district_id.length === 0;


  const handleApplyFilter = () => {
    // If user didn't select anything → do nothing
    if (isFilterEmpty(filters)) return;

    // Only update if changed
    if (JSON.stringify(filters) !== JSON.stringify(AppliedFilters)) {
      setAppliedFilters(filters);
    }
    setFilterOpen(false);
  };
  const handleRemoveFilter = (section: keyof IDashboardFilterData, id: number | string) => {
    setFilters((prev) => {
      const selected = prev[section];
      return {
        ...prev,
        [section]: selected.filter((item) => item !== id),
      };
    });
    setAppliedFilters((prev) => {
      const selected = prev[section];
      return {
        ...prev,
        [section]: selected.filter((item) => item !== id),
      };
    });
  }

  const appliedFilterCount = AppliedFilters.department_id.length + AppliedFilters.sector_id.length + AppliedFilters.priority_id.length + AppliedFilters.district_id.length;
  const handleClearFilter = () => {
    // If applied filter is already empty → no need to clear again
    if (isFilterEmpty(AppliedFilters)) return;

    const empty = {
      department_id: [],
      sector_id: [],
      priority_id: [],
      district_id: [],
    };

    setFilters(empty);
    setAppliedFilters(empty);
    setFilterOpen(false);
  };


  useEffect(() => {
    if (!isDepartmentsDataLoading || !isSectorsDataLoading || !isPrioritiesDataLoading || !isLocationDataLoading || !isDistrictDataLoading) {
      fetchFilterData()
    }
  }, [fetchFilterData, isDepartmentsDataLoading, isSectorsDataLoading, isPrioritiesDataLoading, isLocationDataLoading, isDistrictDataLoading]);

  useEffect(() => {
    if (auth?.user_id && activeTab === "dashboard" && canOpenTab("dashboard")) {
      fetchProjects();
    }

    return () => {
      projectsAbortRef.current?.abort();
    };
  }, [auth?.user_id, AppliedFilters, activeTab, fetchProjects, canOpenTab]);

  useEffect(() => {
    setSiteTitle(envConfig.APP_TITLE);
  }, []);

  useEffect(() => {
    return () => {
      filtersAbortRef.current?.abort();
    };
  }, []);
  const slectetedSectors = useMemo(() => sectorsData.filter((sector: ISector) => AppliedFilters.sector_id.includes(sector.sector_id)), [sectorsData, AppliedFilters]);
  const slectetedSectorPriorities = useMemo(() => prioritiesData.filter((priority: ILookup) => AppliedFilters.priority_id.includes(priority.data_key)), [prioritiesData, AppliedFilters]);
  const slectetedDepartments = useMemo(() => departmentsData.filter((department: IDepartment) => AppliedFilters.department_id.includes(department.department_id)), [departmentsData, AppliedFilters]);
  const selectedDistrictMunicipality = useMemo(() => districtData.filter((district: IDistrict) => AppliedFilters.district_id.includes(district.district_id)), [districtData, AppliedFilters]);

  const sortedSectors = useMemo(() => [...sectorsData].sort((a, b) => {
    const aSelected = AppliedFilters.sector_id.includes(a.sector_id);
    const bSelected = AppliedFilters.sector_id.includes(b.sector_id);
    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return 0;
  }), [sectorsData, AppliedFilters]);

  const sortedSectorPriorities = useMemo(() => [...prioritiesData].sort((a, b) => {
    const aSelected = AppliedFilters.priority_id.includes(a.data_key);
    const bSelected = AppliedFilters.priority_id.includes(b.data_key);
    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return 0;
  }), [prioritiesData, AppliedFilters]);

  const sortedDepartments = useMemo(() => [...departmentsData].sort((a, b) => {
    const aSelected = AppliedFilters.department_id.includes(a.department_id);
    const bSelected = AppliedFilters.department_id.includes(b.department_id);
    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return 0;
  }), [departmentsData, AppliedFilters]);

  const sortedDistricts = useMemo(() => [...districtData].sort((a, b) => {
    const aSelected = AppliedFilters.district_id.includes(a.district_id);
    const bSelected = AppliedFilters.district_id.includes(b.district_id);
    if (aSelected && !bSelected) return -1;
    if (!aSelected && bSelected) return 1;
    return 0;
  }), [districtData, AppliedFilters]);

  const renderActiveTab = () => {
    if (!canOpenTab(activeTab)) {
      return <AccessDenied />;
    }

    switch (activeTab) {
      case "dashboard":
        return (
          <PipelineStatus
            loading={loading}
            quickStats={quickStats}
            filters={AppliedFilters}
            pipelineOverviewGraph={pipelineOverviewGraph}
            sectorChartData={sectorChartData}
            departmentChartData={departmentChartData}
          />
        );

      case "projects":
        return (
          <div className="tab-pane active h-100">
            <div className="p-4 p-sm-5 px-sm-10">
              <ProjectPipeine filters={AppliedFilters} />
            </div>
          </div>
        );

      case "stagegates":
        return (
          <div className="tab-pane active h-100">
            <div className="p-4 p-sm-5 px-sm-10">
              <StageGates
                setShowCreateModal={setShowCreateModal}
                filters={AppliedFilters}
                refreshData={refreshData}
                setRefreshData={setRefreshData}
              />
            </div>
          </div>
        );

      case "evaluation":
        return (
          <div className="tab-pane active h-100">
            <div className="p-4 p-sm-5 px-sm-10">
              <MyProjectCard filters={AppliedFilters} />
            </div>
          </div>
        );

      case "prospectus":
        return (
          <div className="tab-pane active h-100">
            <div className="p-4 p-sm-5 px-sm-10">
              <InvesterProspectors filters={AppliedFilters} />
            </div>
          </div>
        );

      case "kpi_monitoring":
        return (
          <div className="tab-pane active h-100">
            <div className="p-4 p-sm-5 p-sm-10">
              <KpiMonitoring
                setShowCreateModal={setShowCreateModal}
                filters={AppliedFilters}
                refreshData={refreshData}
                setRefreshData={setRefreshData} />
            </div>
          </div>
        );

      case "reports":
        return (
          <div className="tab-pane active h-100">
            <div className="p-4 p-sm-5 px-sm-10">
              <SGReports />
            </div>
          </div>
        );

      case "settings":
        return (
          <div className="tab-pane active h-100">
            <div className="p-4 p-sm-5 px-sm-10">
              <UserTable />
            </div>
          </div>
        );

      default:
        return null;
    }
  };
  return (
    <div className="pt-0 main-container">
      <div className="w-100">
        {/* Tabs Container */}
        <div className="d-flex flex-column">
          {/* Navigation Tabs */}
          <DashboardMenu
            activeTab={activeTab}
            handleNavigate={handleNavigate}
          />

          {/* Content Area with Sidebar */}
          <div className="d-block w-100 h-100 pt-4 position-relative">
            <div className="container-area-dashboard h-100 d-flex flex-nowrap min-vh-100 mw-100">
              {/* Sidebar */}
              <div
                className={`border-end left-filter-sidebar bg-light flex-shrink-0 apple-spring glass transition-all ${filterOpen ? "sidebar-open" : "sidebar-closed"
                  }`}
                style={{ width: filterOpen ? "20rem" : "4rem" }}
              >
                {/* Filter Toggle */}
                <div className="p-3 border-bottom d-flex gap-2">
                  <div className="filter-toggle-button">
                    <button
                      onClick={() => setFilterOpen(!filterOpen)}
                      className="apple-button-secondary border rounded-4"
                    >
                      <Filter size={16} />
                    </button>
                    {appliedFilterCount > 0 && (
                      <OverlayTrigger
                        placement="bottom"
                        trigger={"click"}
                        rootClose
                        overlay={
                          <Tooltip
                            className={`
          text-left bg-light p-2
          ${window.innerWidth < 768 ? 'tooltip-size-md' : 'tooltip-size-lg'}
        `}
                            id={`tooltip-filter-data`}>

                            <div className="d-flex flex-column">
                              {
                                slectetedSectors.length > 0 && (
                                  <div className="d-flex flex-column mt-3">
                                    <h6>Sectors</h6>
                                    <div className="d-flex flex-wrap gap-2">
                                      {slectetedSectors.map((sector: ISector) => <CustomBadge key={sector.sector_id} onClick={() => handleRemoveFilter("sector_id", sector.sector_id)}>{sector.name}</CustomBadge>)}
                                    </div>
                                  </div>
                                )
                              }
                              {
                                slectetedSectorPriorities.length > 0 && (
                                  <div className="d-flex flex-column mt-4">
                                    <h6>Timeline Priority</h6>
                                    <div className="d-flex flex-row flex-wrap gap-2">
                                      {slectetedSectorPriorities.map((priority: ILookup) => <CustomBadge key={priority.data_key} onClick={() => handleRemoveFilter("priority_id", priority.data_key)}>{priority.display_name}</CustomBadge>)}
                                    </div>
                                  </div>
                                )
                              }
                              {
                                slectetedDepartments.length > 0 && (
                                  <div className="d-flex flex-column mt-4">
                                    <h6>Department</h6>
                                    <div className="d-flex flex-row flex-wrap gap-2">
                                      {slectetedDepartments.map((department: IDepartment) => <CustomBadge key={department.department_id} onClick={() => handleRemoveFilter("department_id", department.department_id)}>{department.department_name}</CustomBadge>)}
                                    </div>
                                  </div>
                                )
                              }
                              {
                                selectedDistrictMunicipality.length > 0 && (
                                  <div className="d-flex flex-column mt-4">
                                    <h6>District Municipality</h6>
                                    <div className="d-flex flex-row flex-wrap gap-2">
                                      {selectedDistrictMunicipality.map((district: IDistrict) => <CustomBadge key={district.district_id} onClick={() => handleRemoveFilter("district_id", district.district_id)}>{district.district_name}</CustomBadge>)}
                                    </div>
                                  </div>
                                )
                              }
                              <button
                                onClick={handleClearFilter}
                                className="apple-button-secondary border rounded-4 w-100 mt-4"
                              >
                                Clear All
                              </button>
                            </div>
                          </Tooltip>
                        }
                      >

                        <span className="badge badge-primary cursor-pointer">{appliedFilterCount}</span>
                      </OverlayTrigger>
                    )}

                  </div>
                  {filterOpen && <button
                    onClick={handleClearFilter}
                    className="apple-button-secondary border rounded-4 w-100"
                  >
                    Clear Filter
                  </button>}
                </div>
                {filterOpen && (
                  <div className="p-5 d-flex flex-column gap-3 border-bottom">
                    <Button
                      onClick={handleApplyFilter}
                    >
                      Apply Filter
                    </Button>
                  </div>
                )}



                {/* Filter Content */}
                {filterOpen && (
                  <div className="accordion" id="filterAccordion" style={{ width: '20rem', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>


                    {/* Sectors */}
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingSectors">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseSectors"
                          aria-expanded="false"
                          aria-controls="collapseSectors"
                        >
                          <h4>Sectors {AppliedFilters.sector_id.length > 0 ? <span className="text-muted ms-2 fs-6">({AppliedFilters.sector_id.length} selected)</span> : <></>}</h4>
                        </button>
                      </h2>
                      <div
                        id="collapseSectors"
                        className="accordion-collapse collapse"
                        aria-labelledby="headingSectors"
                        data-bs-parent="#filterAccordion"
                      >
                        <div className="accordion-body p-3">

                          {sortedSectors.map((sector) => (
                            <div
                              key={sector.sector_id}
                              className="d-flex align-items-center my-1 p-2 cursor-pointer"
                              onClick={() => handleFilterChange("sector_id", sector.sector_id)}
                            >
                              <div onClick={(e) => e.stopPropagation()}>
                                <input
                                  className="form-check-input me-2 w-20px h-20px"
                                  type="checkbox"
                                  checked={filters.sector_id.includes(sector.sector_id)}
                                  onChange={() => handleFilterChange("sector_id", sector.sector_id)}
                                />
                              </div>

                              <span className="h5 fw-normal mb-0 text-gray-700">
                                {sector.name}
                              </span>
                            </div>

                          ))}


                        </div>
                      </div>
                    </div>
                    {/* Sectors Timeline Priority*/}
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingSectorsPriority">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseSectorsPriority"
                          aria-expanded="false"
                          aria-controls="collapseSectorsPriority"
                        >
                          <h4>Timeline Priority {AppliedFilters.priority_id.length > 0 ? <span className="text-muted ms-2 fs-6">({AppliedFilters.priority_id.length} selected)</span> : <></>}</h4>
                        </button>
                      </h2>
                      <div
                        id="collapseSectorsPriority"
                        className="accordion-collapse collapse"
                        aria-labelledby="headingSectorsPriority"
                        data-bs-parent="#filterAccordion"
                      >
                        <div className="accordion-body p-3">

                          {sortedSectorPriorities.map((sectorPrority) => (
                            <div
                              key={sectorPrority.data_key}
                              onClick={() => handleFilterChange("priority_id", sectorPrority.data_key)}
                              className="d-flex align-items-center my-1 p-2 cursor-pointer"
                            >
                              <div onClick={(e) => e.stopPropagation()}>
                                <input className="form-check-input me-2 w-20px h-20px"
                                  type="checkbox"
                                  checked={filters.priority_id.includes(sectorPrority.data_key)}
                                  onChange={() => handleFilterChange("priority_id", sectorPrority.data_key)}
                                />
                              </div>
                              <span className="h5 fw-normal mb-0 text-gray-700">{sectorPrority.display_name}</span>
                            </div>
                          ))}

                        </div>
                      </div>
                    </div>
                    {/* Departments */}
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingDepartments">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseDepartments"
                          aria-expanded="false"
                          aria-controls="collapseDepartments"
                        >
                          <h4>Department {AppliedFilters.department_id.length > 0 ? <span className="text-muted ms-2 fs-6">({AppliedFilters.department_id.length} selected)</span> : <></>}</h4>
                        </button>
                      </h2>
                      <div
                        id="collapseDepartments"
                        className="accordion-collapse collapse"
                        aria-labelledby="headingDepartments"
                        data-bs-parent="#filterAccordion"
                      >
                        <div className="accordion-body p-3 maxh-400px overflow-y-auto">

                          {sortedDepartments.map((department) => (
                            <div
                              key={department.department_id}
                              onClick={() => handleFilterChange("department_id", department.department_id)}
                              className="d-flex align-items-center my-1 p-2 cursor-pointer"
                            >
                              <div onClick={(e) => e.stopPropagation()}>
                                <input className="form-check-input me-2 w-20px h-20px"
                                  type="checkbox"

                                  checked={filters.department_id.includes(department.department_id)}
                                  onChange={() => handleFilterChange("department_id", department.department_id)}
                                />
                              </div>
                              <span className="h5 fw-normal mb-0 text-gray-700">{department.department_name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Locations */}
                    <div className="accordion-item">
                      <h2 className="accordion-header" id="headingLocatons">
                        <button
                          className="accordion-button collapsed"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseLocations"
                          aria-expanded="false"
                          aria-controls="collapseLocations"
                        >
                          <h4>District Municipality {AppliedFilters.district_id.length > 0 ? <span className="text-muted ms-2 fs-6">({AppliedFilters.district_id.length} selected)</span> : <></>}</h4>
                        </button>
                      </h2>
                      <div
                        id="collapseLocations"
                        className="accordion-collapse collapse"
                        aria-labelledby="headingLocatons"
                        data-bs-parent="#filterAccordion"
                      >
                        <div className="accordion-body p-3 maxh-400px overflow-y-auto">

                          {sortedDistricts.map((district) => (
                            <div
                              key={district.district_id}
                              onClick={() => handleFilterChange('district_id', district.district_id)}
                              className="d-flex align-items-center my-3 p-2 cursor-pointer"
                            >
                              <div onClick={(e) => e.stopPropagation()}>
                                <input className="form-check-input me-2 w-20px h-20px"
                                  type="checkbox"
                                  checked={filters.district_id.includes(district.district_id)}
                                  onChange={() => handleFilterChange('district_id', district.district_id)}
                                />
                              </div>
                              <span className="h5 fw-normal mb-0 text-gray-700">{district.district_name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Main Content */}
              <div className="flex-grow-1 min-w-0">
                <div className="tab-content h-100">
                  {renderActiveTab()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CreateProjectModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateProject={onCreateProject}
        districts={districtData}
        departments={departmentsData}
        sectors={sectorsData}
        priorities={prioritiesData}
        handleFetchMunicipalityData={handleFetchMunicipalityData}
        municipalityData={municipalityData}
      />

    </div>
  );
};

export { DashBoardNew };